import React from 'react';
import classNames from 'classnames/bind';
import AirlineLogo from 'common/airlineLogo';
import styles from './detailScheduleLayer.module.scss';
import { IScheduleDetail, ScheduleKeys } from 'list/international/types';
import {
  getDifferentNextAirport,
  getHasNightTransfer,
  getIsArrivalAtNight,
  getIsDepartureAtNight,
  getIsLongWaitingTime,
  getScheduleTitle,
} from './util';
import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import { ScheduleType } from 'contexts/searchParams';
import { IKeyStringValue } from 'common/types';
import { showFlightTime } from 'detail/utils';

const cx = classNames.bind(styles);

const Labels = ({
  isDepartureAtNight,
  isArrivalAtNight,
  hasNightTransfer,
  differentNextAirport,
  isLongWaitingTime,
}: {
  isDepartureAtNight: boolean;
  isArrivalAtNight: boolean;
  hasNightTransfer: boolean;
  differentNextAirport: boolean;
  isLongWaitingTime: boolean;
}) => {
  const hasAnyLabel =
    isDepartureAtNight ||
    isArrivalAtNight ||
    hasNightTransfer ||
    differentNextAirport ||
    isLongWaitingTime;

  if (!hasAnyLabel) return null;
  return (
    <div className={cx('label_area')}>
      {differentNextAirport && (
        <span className={cx('label', 'change')}>
          <i className={cx('caution')} title="주의사항" />
          공항변경
        </span>
      )}
      {isLongWaitingTime && (
        <span className={cx('label', 'warning')}>
          <i className={cx('caution')} title="주의사항" />긴 대기시간
        </span>
      )}
      {hasNightTransfer && (
        <span className={cx('label', 'warning')}>
          <i className={cx('caution')} title="주의사항" />
          야간환승
        </span>
      )}
      {isDepartureAtNight && (
        <span className={cx('label', 'warning')}>
          <i className={cx('caution')} title="주의사항" />
          야간출발
        </span>
      )}
      {isArrivalAtNight && (
        <span className={cx('label', 'warning')}>
          <i className={cx('caution')} title="주의사항" />
          야간도착
        </span>
      )}
    </div>
  );
};

interface IProp {
  schedule: IScheduleDetail[];
  onClose: Function;
  scheduleType?: ScheduleType;
  airlines: IKeyStringValue;
  airports: IKeyStringValue;
}

export default function DetailScheduleLayer({
  onClose,
  schedule,
  scheduleType,
  airlines,
  airports,
}: IProp) {
  // @ts-ignore
  const ref = useOffClick(onClose, { stopPropagation: true });

  const scheduleDetails = schedule.map((sch, index, sches) => {
    const {
      [ScheduleKeys.startAirport]: startAirportCode,
      [ScheduleKeys.startDateTime]: startDateTime,
      [ScheduleKeys.endAirport]: endAirportCode,
      [ScheduleKeys.endDateTime]: endDateTime,
      [ScheduleKeys.airlineVendor]: airlineCode,
      [ScheduleKeys.cooperatedAirlineVendor]: cooperatedAirlineCode,
      [ScheduleKeys.flightNumber]: flightNumber,
      [ScheduleKeys.flightTime]: flightTime,
      [ScheduleKeys.waitingTime]: waitingTime,
      [ScheduleKeys.hiddenAirports]: hiddenAirports,
    } = sch;

    // 야간 출발
    const isDepartureAtNight = getIsDepartureAtNight(sch);

    // 야간 도착
    const isArrivalAtNight = getIsArrivalAtNight(sch);

    // 야간 환승
    const hasNightTransfer = getHasNightTransfer(sch);

    // 공항변경
    const differentNextAirport = getDifferentNextAirport(sches, index);

    // 긴 대기시간
    const isLongWaitingTime = getIsLongWaitingTime(sch);

    return {
      startAirportCode,
      endAirportCode,
      isDepartureAtNight,
      isArrivalAtNight,
      hasNightTransfer,
      differentNextAirport,
      isLongWaitingTime,
      airlineCode,
      airlineName: airlines[airlineCode],
      cooperatedAirlineCode,
      cooperatedAirlineName: airlines[cooperatedAirlineCode],
      flightNumber,
      flightTime: showFlightTime(flightTime),
      waitingTime: !!+waitingTime && showFlightTime(waitingTime),
      hiddenAirports: hiddenAirports?.map(hiddenAirport => ({
        airportCode: hiddenAirport.ap,
        waitingTime: hiddenAirport.st,
      })),
      departureTitle: getScheduleTitle({
        airportCode: startAirportCode,
        flightDateTime: startDateTime,
        airports,
      }),
      arrivalTitle: getScheduleTitle({
        airportCode: endAirportCode,
        flightDateTime: endDateTime,
        airports,
      }),
    };
  });

  const itineraryCount = schedule.length;
  const title = <>{airports[schedule[0][ScheduleKeys.startAirport]]}<span className={cx('icon_flight')} aria-label="" />{airports[schedule[itineraryCount - 1][ScheduleKeys.endAirport]]}</>

  return (
    <div className={cx('DetailScheduleLayer')} ref={ref}>
      <b className={cx('title')}>{title}</b>
      <div className={cx('scroll_area')}>
        <div className={cx('section')}>
          {scheduleDetails.map(
            (
              {
                departureTitle,
                arrivalTitle,
                airlineCode,
                airlineName,
                cooperatedAirlineCode,
                cooperatedAirlineName,
                flightNumber,
                flightTime,
                waitingTime,
                isDepartureAtNight,
                isArrivalAtNight,
                isLongWaitingTime,
                hasNightTransfer,
                differentNextAirport,
                hiddenAirports,
              },
              index,
            ) => {
              return (
                <div className={cx('trip')} key={index}>
                  <div className={cx('route')}>
                    <b className={cx('name')}>{departureTitle}</b>
                    <figure className={cx('logo')}>
                      <AirlineLogo
                        airlineCode={airlineCode}
                        alt={airlineCode}
                        className={cx('img')}
                      />
                    </figure>
                    <div className={cx('info')}>
                      {airlineCode}
                      {flightNumber}{' '}
                      {cooperatedAirlineCode
                        ? cooperatedAirlineName
                          ? `${cooperatedAirlineName} 공동운항`
                          : '공동운항 정보확인필요'
                        : airlineName}
                      <br />
                      비행 {flightTime}
                    </div>
                  </div>

                  {hiddenAirports &&
                    hiddenAirports.length > 0 &&
                    hiddenAirports.map(
                      ({ airportCode, waitingTime }, i: number) => {
                        // airportCode, waitingTime 가 '' 있을때가 있음
                        if (!airportCode || !waitingTime) return null;
                        return (
                          <div className={cx('route')} key={i}>
                            <b
                              className={cx('name')}
                            >{`${airports[airportCode] || ''} 체류`}</b>
                            <figure className={cx('logo')}>
                              <AirlineLogo
                                airlineCode={airlineCode}
                                alt={airlineName}
                                className={cx('img')}
                              />
                            </figure>
                            <i className={cx('info')}>
                              {airlineCode}
                              {flightNumber}{' '}
                              {cooperatedAirlineCode
                                ? cooperatedAirlineName
                                  ? `${cooperatedAirlineName} 공동운항`
                                  : '공동운항 정보확인필요'
                                : airlineName}
                              <br />
                              {!!+waitingTime &&
                                `대기 ${showFlightTime(waitingTime)}`}
                            </i>
                          </div>
                        );
                      },
                    )}

                  <div className={cx('route')}>
                    <b className={cx('name')}>{arrivalTitle}</b>
                    {waitingTime && (
                      <div className={cx('info')}>대기 {waitingTime}</div>
                    )}
                    <Labels
                      isDepartureAtNight={isDepartureAtNight}
                      isArrivalAtNight={isArrivalAtNight}
                      isLongWaitingTime={isLongWaitingTime}
                      hasNightTransfer={hasNightTransfer}
                      differentNextAirport={differentNextAirport}
                    />
                  </div>
                </div>
              );
            },
          )}
        </div>
      </div>
      <button
        className={cx('btn_close')}
        type="button"
        onClick={() => onClose()}
      />
    </div>
  );
}
